import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { branch, compose, renderComponent } from 'recompose';

import {
  LENGTH_OF_PERIOD_OPTIONS,
  PAYROLL_KIND_OPTIONS,
  PAYROLL_KINDS,
} from '../settings/commission/constants';
import Switch from '../../common/checkboxSwitch';
import Tooltip from '../../common/tooltip';
import Select from '../../common/select';
import { DAYS_OF_WEEK_OPTIONS } from '../constants';
import { payrollKindShape } from '../settings/commission/shapes';
import PercentageInput from '../../common/percentageInput';
import CommissionSplitFees from './commissionSplitFees';
import { flagNames, withInjectFeatureFlagsToProps } from '../../../modules/infra/feature-flags';

const CommissionSplit = ({ payrollKind, payrollEnabled }) => (
  <Fragment>
    <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">
      Commission Shop Payout Settings
    </div>
    <div className="c-wrapper-horizontal u-pb--">
      <div className="layout layout--auto">
        <div className="layout__item">
          <div className="o-flexbox o-flexbox--content-align-center">
            <Switch
              label={(
                <span>
                  Enabled
                  <Tooltip className="u-type--uppercase">
                    When this flag is off, all of the transactions, including tips and taxes,
                    will be deposited to the shop&apos;s bank account.
                  </Tooltip>
                </span>
              )}
              name="payrollEnabled"
            />
          </div>
        </div>
      </div>
      <div className="layout u-1/2 u-mt-">
        <div className="layout__item">
          <span className="c-label-new">
            Payout Mode
            <Tooltip>
              Each mode will route tips to the barber provided bank account immediately after each transaction.
              If the Auto Tip and Commission Splits mode is selected,
              the commission payments will be processed at the end of the week
              and transferred to the barber provided bank accounts.
              If the Auto Tip Only mode is selected, all money other than tips will be transferred
              to the shop account at the end of each day and the shop will be required
              to payout commission manually (not through the Squire system).
            </Tooltip>
          </span>
          <Select
            className="c-select"
            name="payrollKind"
            options={PAYROLL_KIND_OPTIONS}
          />
        </div>
      </div>
      <div
        display-if={payrollEnabled && payrollKind === PAYROLL_KINDS.dailyPayroll}
        className="layout u-1/2 u-mt-"
      >
        <div className="layout__item">
          <div className="c-label-new">Auto Tip Only Fee</div>
          <PercentageInput
            className="c-input"
            name="dailyPayrollTipFee"
            parse={value => Number(value)}
          />
        </div>
      </div>
      <div display-if={payrollEnabled && payrollKind === PAYROLL_KINDS.commissionSplits}>
        <div className="layout layout--auto u-mt-">
          <div className="layout__item u-mt-">
            <div className="c-label-new">One Touch Payout Fee</div>
            <PercentageInput
              className="c-input"
              name="commissionSplitsPercentFee"
              parse={value => Number(value)}
            />
          </div>
        </div>
        <div className="layout layout--auto u-mt">
          <div className="layout__item">
            <Switch
              label="Cash to Barber - Services"
              name="commissionSplitsCashToBarbersServices"
            />
          </div>
        </div>
        <div className="layout layout--auto u-mt-">
          <div className="layout__item">
            <Switch
              label="Cash to Barber - Products"
              name="commissionSplitsCashToBarbersProducts"
            />
          </div>
        </div>
        <div className="layout layout--auto u-mt-">
          <div className="layout__item">
            <Switch
              label="Shop pays fees"
              name="commissionSplitsShopPaysFees"
            />
          </div>
        </div>
        <div className="layout u-mt">
          <div className="layout__item u-1/2">
            <span className="c-label-new">End of Pay Period Day</span>
            <Select
              className="c-select"
              name="commissionPayoutDayOfWeek"
              options={DAYS_OF_WEEK_OPTIONS}
              parse={val => parseInt(val, 10)}
              empty
            />
          </div>
          <div className="layout__item u-1/2">
            <span className="c-label-new">Length of Period</span>
            <Select
              className="c-select"
              name="commissionPayoutPeriodInDays"
              options={LENGTH_OF_PERIOD_OPTIONS}
              parse={val => parseInt(val, 10)}
              empty
            />
          </div>
        </div>
      </div>
    </div>
  </Fragment>
);

CommissionSplit.propTypes = {
  payrollKind: payrollKindShape.isRequired,
  payrollEnabled: PropTypes.bool.isRequired,
};

export default compose(
  withInjectFeatureFlagsToProps([flagNames.AUTOPAYOUT_SELF_SERVE]),
  branch(
    props => props[flagNames.AUTOPAYOUT_SELF_SERVE],
    renderComponent(CommissionSplitFees),
    renderComponent(CommissionSplit),
  ),
)(null);
